/**
 * Where the global "Ask CoPilot" hook (AssistantContextService) puts its link (CU-14zefhy1qnh): ng-zorro error modals
 * (`NzModalService.error`), confirm modals with error styling (`NzModalService.confirm` with the error icon), the app's
 * own message dialog when it shows an error (`DialogService.showMessage`, marked with ERROR_DIALOG_CLASS) and ng-zorro
 * error notifications / messages. Toastr error toasts have the link in their own template (AlertToastComponent).
 *
 * No Angular imports: unit-tested without a TestBed.
 */
import {ERROR_DIALOG_CLASS} from 'app/common/dialog/dialog-error';
import {ASSISTANT_LABEL} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-label';

export type AskTargetKind = 'error-modal' | 'error-confirm' | 'app-error-dialog' | 'notice';

/** every box that may get the link; `askTargetKind` decides (a plain confirm is matched here and then skipped) */
export const ASKABLE_ERRORS = [
    '.ant-modal-confirm-error',
    '.ant-modal-confirm-confirm',
    `.ant-modal.${ERROR_DIALOG_CLASS}`,
    '.ant-notification-notice-error',
    '.ant-message-error'
].join(', ');

/**
 * the icon ng-zorro shows on an error modal; a confirm that uses it is styled as an error. Only the confirm's own icon
 * (a direct child of the confirm body) counts: the modal's content may hold other close-circle icons (allowClear on
 * nz-input / nz-select, di-search-bar) that do not make the confirm an error.
 */
export const ERROR_ICON = '.ant-modal-confirm-body > .anticon-close-circle';

/** whether a confirm box shows the error icon as its own icon (see ERROR_ICON) */
export function hasErrorIcon(box: ParentNode | null | undefined): boolean {
    return !!box?.querySelector(ERROR_ICON);
}

export interface AskTargetParts {
    /** elements whose text (joined with ": ") is the error the user asks about; read when the link is clicked */
    text: string[];
    /** where the link goes (null: next to the text) */
    slot: string | null;
    /** button that closes the box after the click so the chat is usable (null: leave it); a confirm is cancelled, never confirmed */
    close: string | null;
}

export const ASK_TARGET_PARTS: {[kind in AskTargetKind]: AskTargetParts} = {
    'error-modal': {text: ['.ant-modal-confirm-body'], slot: '.ant-modal-confirm-btns', close: '.ant-modal-confirm-btns button'},
    'error-confirm': {
        text: ['.ant-modal-confirm-body'],
        slot: '.ant-modal-confirm-btns',
        close: '.ant-modal-confirm-btns button:not(.ant-btn-primary)'
    },
    'app-error-dialog': {
        text: ['di-message-dialog h1', 'di-message-dialog .di-text', 'di-message-dialog .di-hint'],
        slot: '.di-footer',
        close: '.di-footer button'
    },
    notice: {text: ['.ant-notification-notice-content, .ant-message-custom-content'], slot: null, close: null}
};

/**
 * Which kind of error box an element is, from its classes; null when it gets no link (a confirm without the error
 * icon, e.g. "Start a new AI session?" with a red OK button, is a question, not an error).
 */
export function askTargetKind(classes: string[] | DOMTokenList, hasErrorIcon: boolean): AskTargetKind | null {
    const has = (c: string) => Array.prototype.indexOf.call(classes || [], c) >= 0;
    if (has('ant-modal-confirm-error')) {
        return 'error-modal';
    }
    if (has('ant-modal-confirm-confirm')) {
        return hasErrorIcon ? 'error-confirm' : null;
    }
    if (has(ERROR_DIALOG_CLASS)) {
        return 'app-error-dialog';
    }
    if (has('ant-notification-notice-error') || has('ant-message-error')) {
        return 'notice';
    }
    return null;
}

/** The asked-about text from the pieces of a box: blanks and the link's own label dropped, joined with ": ". */
export function askErrorText(parts: Array<string | null | undefined>): string {
    return parts
        .map((p) =>
            String(p || '')
                .split(ASSISTANT_LABEL)
                .join('')
                .replace(/\s+/g, ' ')
                .trim()
        )
        .filter((p, i, all) => !!p && all.indexOf(p) === i)
        .join(': ');
}

/** The "Ask HaulierMagic" link of an error box: its label is ASSISTANT_LABEL, which askErrorText strips from the asked-about text. */
export function buildAskLink(doc: Pick<Document, 'createElement'>): HTMLAnchorElement {
    const link = doc.createElement('a');
    link.className = 'hm-ask-copilot';
    link.setAttribute('data-ai-ignore', '');
    link.setAttribute('role', 'button');
    link.textContent = ASSISTANT_LABEL;
    link.style.cssText = 'margin-left:8px;cursor:pointer;text-decoration:underline;font-weight:600;white-space:nowrap';
    return link;
}

/** The error text of an error box: its text pieces in order, else the whole box. */
function boxText(box: Element, selectors: string[]): string {
    const read = (el: Element | null) => (el ? (el as HTMLElement).innerText || el.textContent || '' : '');
    const text = askErrorText(selectors.map((s) => read(box.querySelector(s))));
    return text || askErrorText([read(box)]);
}

/**
 * Puts the "Ask HaulierMagic" link into every askable error box under host that has none yet. A click calls onAsk with the
 * box text (read at click time: the app's error dialog is reused for the next error) and then closes the dialog with its own button.
 */
export function insertAskLinks(host: Element, doc: Pick<Document, 'createElement'>, onAsk: (text: string) => void): void {
    host.querySelectorAll(ASKABLE_ERRORS).forEach((box) => {
        if (box.querySelector('.hm-ask-copilot')) {
            return;
        }
        const kind = askTargetKind(box.classList, hasErrorIcon(box));
        if (!kind) {
            return;
        }
        const parts = ASK_TARGET_PARTS[kind];
        const link = buildAskLink(doc);
        link.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            onAsk(boxText(box, parts.text));
            if (parts.close) {
                (box.querySelector(parts.close) as HTMLElement)?.click();
            }
        });
        const slot = parts.slot ? box.querySelector(parts.slot) : box.querySelector(parts.text.join(', '));
        (slot || box).appendChild(link);
    });
}
